/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import "../../../components/Drawers/DeviceDrawer.css";
import { IDuCellInfo } from "../../../store/api/devices/types";
import StatusIcon from "../../../components/Shared/Status/StatusIcon";
import AutocompleteSelect from "../../../components/Shared/Dropdown/AutocompleteSelect";
import { IOption } from "../../../components/Shared/Filter/types";
import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../../globals/constants";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { setConfirmDialog } from "../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../store/hooks";
import { confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { useSelector } from "react-redux";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { IDusNodeDataGridRow, IFullDeviceDetails, INrCell } from "../../../store/api/deviceConfig/types";
import { setOpenRnCuDrawer } from "../../../store/api/devices/reducers";
import { resetDuCellInfo } from "../helpers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { InputField } from "../../../components/Shared/Radix/Inputs/InputField";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { Loader2, Trash2 } from "lucide-react";

interface IPayload {
  [key: string]: string;
}
interface IRadioCellInfo {
  selectedRadioNode?: IDusNodeDataGridRow;
  cellFormData?: IDuCellInfo;
  isCreateRadioCell?: boolean;
  cellType?: string;
  zones?: IOption[];
  zoneOption?: IOption[];
  selectedBand?: string;
  isAddEditType?: string;
  setCellFormData: (formData: IDuCellInfo, type: string) => void;
  handleSelectBand?: (val: string, type: string) => void;
  handleUpdateCellInfoOption?: (args: boolean, type: string) => void;
  handleSetSelectedZones?: (newValue: any | null, type: string) => void;
  handleSubmit: (type: string) => void;
  lteCellList?: string[];
  onBlurHandler?: (event: any) => void;
  errMsg?: IPayload;
  configStatus?: boolean;
  bandData?: string;
  nrCellListData?: INrCell[];
  handleRemovesZones: () => void;
  initialZones?: IOption[];
  isProvisioned?: boolean;
  handleCancel: (type: string) => void;
  isCellDrawer?: string;
}

const AddEditCuInformation: React.FC<IRadioCellInfo> = ({
  selectedRadioNode,
  cellFormData,
  isCreateRadioCell = false,
  cellType = "",
  zones,
  zoneOption,
  selectedBand = "",
  isAddEditType = "add",
  setCellFormData,
  handleUpdateCellInfoOption,
  handleSelectBand,
  handleSetSelectedZones,
  handleSubmit,
  lteCellList = [],
  onBlurHandler,
  errMsg,
  configStatus,
  bandData = "",
  nrCellListData = [],
  handleRemovesZones,
  initialZones = [],
  isProvisioned = false,
  handleCancel,
  isCellDrawer = "",
}) => {
  const dispatch = useStoreDispatch();
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const handleChange = (name: string, value: string) => {
    let newformData = { ...cellFormData, [name]: value };
    if (setCellFormData) {
      setCellFormData(newformData, cellType);
    }
  };

  const handleDeleteCell = (actionName: string) => {
    if (!actionName) return;
    handleSubmit("delete");
  };

  const handleDeleteCellModal = () => {
    if (Array.isArray(initialZones) && initialZones?.length > 0) {
      dispatch(setConfirmDialog(DIALOGOPENFOR.REMOVECUZONE));
    } else dispatch(setConfirmDialog(DIALOGOPENFOR.CELLDELETE));
  };

  const autocompleteSelectHandler = (event: any, newValue: any | null) => {
    if (handleSetSelectedZones) {
      handleSetSelectedZones(newValue, cellType);
    }
  };

  const handleSelect = (value: string) => {
    if (handleSelectBand) {
      handleSelectBand(value, cellType);
    }
  };

  const handleSubmitCellConfig = () => {
    if (handleSubmit) {
      if (isProvisioned) {
        handleSubmit("provision");
      } else handleSubmit(isAddEditType);
    }
  };

  const handleRemoveZoneData = (actionName: string) => {
    if (!actionName) return;
    handleRemovesZones();
  };

  const handleIsCreateCell = () => {
    let isDisabled = false;
    if (cellType === "radio1") {
      if (Array.isArray(nrCellListData) && nrCellListData?.length > 0) {
        let cellObj: any = {};
        cellObj = nrCellListData?.find((el: INrCell) => Number(el?.radioNode) === selectedRadioNode?.id);
        if (!!cellObj?.index) {
          isDisabled = true;
        }
      }
    }
    return isDisabled;
  };

  const handleRemoveCell = () => {
    if (!isProvisioned) {
      dispatch(setOpenRnCuDrawer(false));
    } else {
      setCellFormData(resetDuCellInfo, cellType);
      handleCancel(cellType);
    }
  };

  return (
    <>
      {openConfirmDialog === DIALOGOPENFOR.CELLDELETE && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteCell}>
          Are you sure you want to delete <strong>{cellFormData?.cellName || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      {openConfirmDialog === DIALOGOPENFOR.REMOVECUZONE && (
        <ConfirmDialog actionName={constStr.ok} handleClickAction={handleRemoveZoneData}>
          Applying this delete action will remove cells on the zones added. Are you sure you want to delete{" "}
          <strong>{cellFormData?.cellName || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      <div className="w-full flex justify-between items-center">
        <div>
          <h5 className="text-xl font-semibold text-foreground mb-2">Cell Information</h5>
          <StatusIcon status={cellFormData?.status || ""} />
        </div>
        <div>
          {permissions?.hasOpCodeGroup("manage_topology") && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="border-0"
              title="Delete Cell"
              disabled={configStatus || selectedDevice?.configurationStatus === "OFFLINE"}
              onClick={handleDeleteCellModal}
            >
              {configStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-destructive" />}
            </Button>
          )}
        </div>
      </div>
      <div className="w-full">
        <InputField
          name="cellId"
          label="Cell ID"
          type="number"
          disabled={configStatus || handleIsCreateCell()}
          value={cellFormData?.cellId ?? ""}
          onChange={handleChange}
          onBlur={onBlurHandler}
          min={1}
        />
      </div>
      <div className="w-full">
        <InputField
          name="cellName"
          label="Cell Name"
          disabled={configStatus || selectedDevice?.configurationStatus === "OFFLINE"}
          value={cellFormData?.cellName ?? ""}
          onChange={handleChange}
          onBlur={onBlurHandler}
        />
      </div>
      <div className="w-full">
        <InputField
          name="cellDescription"
          label="Cell Description"
          disabled={configStatus || selectedDevice?.configurationStatus === "OFFLINE"}
          value={cellFormData?.cellDescription ?? ""}
          onChange={handleChange}
          onBlur={onBlurHandler}
        />
      </div>
      <div className="w-full">
        <InputField
          name="cellPci"
          label="Cell PCI"
          type="number"
          disabled={configStatus || selectedDevice?.configurationStatus === "OFFLINE"}
          value={cellFormData?.cellPci ?? ""}
          onChange={handleChange}
          onBlur={onBlurHandler}
          min={1}
        />
      </div>
      {parseFloat(selectedDevice?.swVersion) >= 23.4 ? (
        <div className="w-full">
          <InputField
            name="txPower"
            label="TX power percentage"
            type="number"
            disabled={configStatus || selectedDevice?.configurationStatus === "OFFLINE"}
            value={cellFormData?.txPower ?? ""}
            onChange={handleChange}
            onBlur={onBlurHandler}
          />
        </div>
      ) : (
        <div className="w-full">
          <InputField
            name="pbchPower"
            label="PBCH reference signal power"
            type="number"
            disabled={configStatus || selectedDevice?.configurationStatus === "OFFLINE"}
            value={cellFormData?.pbchPower ?? ""}
            onChange={handleChange}
            onBlur={onBlurHandler}
          />
        </div>
      )}
      <div className="w-full">
        <InputField
          name="commonCellId"
          label="Common Cell ID"
          type="number"
          disabled={configStatus || handleIsCreateCell() || selectedDevice?.configurationStatus === "OFFLINE"}
          value={cellFormData?.commonCellId ?? ""}
          onChange={handleChange}
          onBlur={onBlurHandler}
        />
      </div>
      <div className="w-full space-y-0">
        <Label htmlFor="SelectZones" className="text-sm font-medium">Select the Zones</Label>
        <AutocompleteSelect
          name="zones"
          value={zones}
          multiple={true}
          id={"zoneId"}
          options={zoneOption || []}
          label={""}
          margin={"normal"}
          size={"small"}
          disabled={configStatus || !cellFormData?.cellId || selectedDevice?.configurationStatus === "OFFLINE"}
          onChange={autocompleteSelectHandler}
          disablePortal
        />
      </div>
      {/* <div className="w-full flex justify-end">
        <div className="flex justify-end gap-2">
          <Button variant="outline" disabled={configStatus|| selectedDevice?.configurationStatus === "OFFLINE"} onClick={handleRemoveCell}>
            Cancel
          </Button>
          {permissions?.hasOpCodeGroup("manage_topology") && (
            <Button
              disabled={configStatus || selectedDevice?.status === 19|| selectedDevice?.configurationStatus === "OFFLINE"}
              onClick={handleSubmitCellConfig}
            >
              {configStatus && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isProvisioned ? "Provision" : "Save"}
            </Button>
          )}
        </div>
      </div> */}
    </>
  );
};

export default AddEditCuInformation;
